import { isPresent } from 'utils/isPresent';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash-es';
import ListItemText from 'housecall-ui/dist/DesignSystem/DataDisplay/List/ListItemText';
import ListItem from 'housecall-ui/dist/DesignSystem/DataDisplay/List/ListItem';
import { AutosuggestTextField } from "../../Common/AutosuggestTextField.jsx";
import InputAdornment from 'housecall-ui/dist/DesignSystem/Inputs/TextFields/InputAdornment'
import Grid from "housecall-ui/dist/DesignSystem/Layout/Grid/Grid"
import Typography from "housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography"
import ClearIcon from 'housecall-ui/dist/IconsAndImages/Icons/Close'
import IconButton from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/IconButton';
import CircularProgress from "housecall-ui/dist/DesignSystem/Feedback/Progress/CircularProgress"
import withStyles from 'housecall-ui/dist/DesignSystem/Styles/withStyles';
import { showErrorDialog } from '../../Common/CommonActions.js';
import TruncatedText from "../../Common/TruncatedText.jsx";
import AddOrEditCustomerDialog from "./AddOrEditCustomerDialog.jsx"
import { fetchCustomer } from '../../Job/AddJob/Redux/Actions'
import TextField from 'housecall-ui/dist/DesignSystem/Inputs/TextFields/TextField'
import Utils from '../../Common/Utils.js';
import { isBlank } from 'utils/isBlank';

const ENTER_KEY = 13

const styles = theme => ({
    removeIconHover: {
        '&:hover': {
            fill: theme.palette.error[500]
        }
    },
    customerListItem: {
        padding: 0
    },
    addressTextStyle: {
        color: theme.palette.textColor
    },
    notesTextStyle: {
        color: theme.palette.secondaryTextColor,
        lineHeight: 1.4
    },
    addressStyle: {
        top: 0,
        marginTop: 0
    }
})

@connect(
    state => ({}),
    dispatch => ({
        showErrorDialog: (error) => {
            dispatch(showErrorDialog(Utils.parseErrorMessage(error)))
        },
        fetchCustomer: (customerId) => {
          dispatch(fetchCustomer(customerId))
        }
    })
)
@withStyles(styles)
export default class SearchCustomer extends PureComponent {

    constructor(props) {
        super(props)        
        
        this.state = {
            customers: [],
            inputText: props.initialValue || '',
            showSpinner: false,
            open: false
        }
    }

    setCustomerResults(customers, searchText) {
        let newCustomerList = customers

        if (isBlank(customers) && this.props.shouldAddEmptyCustomer) {
            newCustomerList = [{
                name: 'No available customers found',
                printable_address: <Typography variant="subtitle1" color="primary">
                    + Add new customer
                </Typography>,
                onClick: () => this.setState({ open: true })
            }]
        }

        this.setState({
            customers: newCustomerList,
            showSpinner: false
        })
    }

    onSuggestionsFetchRequested(searchText) {
        if (this.props.disabled) {
            return
        }

        var fetchCustomers = debounce((searchText) => {
            if (isPresent(searchText) && this.state.inputText === searchText) {
                this.props.onSearch(searchText)
                    .then((response) => {
                        this.setCustomerResults(response.data, searchText)
                    })
                    .catch((error) => {
                        this.setState({
                            showSpinner: false
                        })
                        if (error.response) {
                            this.props.showErrorDialog(error)
                        }
                        console.log('failed to search customers: ' + error)
                    })
            }
        }, 900)

        fetchCustomers(searchText)
    }

    clearSearch() {
        this.setState({
            inputText: ''
        })
    }

    onSuggestionSelected(suggestionValue) {

        if (isPresent(suggestionValue.onClick)) {
            this.clearSearch()
            suggestionValue.onClick()
        } else {
            this.props.onSelect(suggestionValue, () => this.clearSearch())
        }

        if (isPresent(this.refs.search_customer)) {
            this.refs.search_customer.blur()
        }

        this.setState({
            customers: []
        })
    }

    nonEditableInputTextDeleteButton() {
        return <IconButton
            style={{ backgroundColor: 'transparent' }}
            onClick={() => {
                this.props.onSelect(null)
                this.clearSearch()
            }}
        >
            <ClearIcon className={this.props.classes.removeIconHover}/>
        </IconButton>
    }

    endAdornmentElement() {
        let element = null
        if (this.props.disabled) {
            element = this.nonEditableInputTextDeleteButton()
        } else if (this.state.showSpinner) {
            element = <CircularProgress size={16}/>
        }

        return isPresent(element) && <InputAdornment position="end">
            {element}
        </InputAdornment>
    }

    getNewInputValue(newValue) {
        return newValue.name || newValue.company_or_name || newValue
    }

    getInputProps() {

        const onChanged = (event, {newValue}) => {
            event.preventDefault()

            this.setState({
                inputText: this.getNewInputValue(newValue),
                showSpinner: isPresent(newValue) && (typeof newValue === 'string')
            })
        }

        const onKeyDown = (event) => {
            if (event.keyCode === ENTER_KEY) {
                event.preventDefault()
            }
        }

        return {
            ref: "search_customer",
            value: this.state.inputText || this.props.value || '',
            onChange: onChanged,
            onKeyDown: onKeyDown,
            label: this.props.label,
            placeholder: this.props.hintText || "Search customers (name, email, phone or address)",
            fullWidth: true,
            disabled: this.props.disabled,
            variant:this.props.variant,
            InputProps: {
                endAdornment: this.endAdornmentElement(),
                ...this.props.InputProps
            }
        }
    };

    render() {
        const {classes} = this.props

        const primaryCustomerText = (suggestion) => {
            return <Grid container justify="space-between" spacing={0}>
                <Grid item>
                    <Typography variant="subtitle1">
                        {suggestion.name || suggestion.company_or_name}
                    </Typography>
                </Grid>
                <Grid item>
                    <Typography variant="body2" className={classes.addressTextStyle}>
                        {suggestion.printable_address}
                    </Typography>
                </Grid>
            </Grid>
        }

        const renderSuggestion = (suggestion, {query, isHighlighted}) => {
            return <ListItem classes={{gutters: classes.customerListItem}}>
                <ListItemText
                    primary={primaryCustomerText(suggestion)}
                    secondary={(this.props.showNotes && isPresent(suggestion.address_notes)) &&
                    <Typography variant="body1" className={classes.notesTextStyle}>
                        <TruncatedText lines={2} children={suggestion.address_notes}/>
                    </Typography>
                    }
                />
            </ListItem>
        }

      const renderInput = ({ref, endAdornment, ...restProps}) => (
          <TextField
              inputRef={ref}
              InputProps={
                {endAdornment: endAdornment}
              }
              {...restProps}
          />
      )

        return <div>
            <AutosuggestTextField
                inputStyle={this.props.inputStyle}
                renderInputComponent={renderInput}
                renderSuggestion={renderSuggestion}
                suggestions={this.state.customers}
                onSuggestionsFetchRequested={({value, reason}) => {
                    this.onSuggestionsFetchRequested(value)
                }}
                onSuggestionsClearRequested={() => {
                    this.setState({
                        customers: []
                    })
                }}
                inputProps={this.getInputProps()}
                onSuggestionSelected={(event, {suggestion, suggestionValue, suggestionIndex, sectionIndex, method}) => {
                    this.onSuggestionSelected(suggestionValue)
                }}
            />
            {this.state.open && <AddOrEditCustomerDialog
              open={this.state.open}
              onSuccess={(customer) => {this.props.fetchCustomer(customer.id)}}
              closeDialog={() => {this.setState({ open: false })}}
            />}
        </div>
    }
}
